import React, { useState } from 'react';
import { MapContainer, TileLayer, Marker, Popup, Tooltip, Polygon } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import markerIcon2x from 'leaflet/dist/images/marker-icon-2x.png';
import markerIcon from 'leaflet/dist/images/marker-icon.png';
import markerShadow from 'leaflet/dist/images/marker-shadow.png';

L.Icon.Default.mergeOptions({
  iconRetinaUrl: markerIcon2x,
  iconUrl: markerIcon,
  shadowUrl: markerShadow
});

// list of key locations (approx coords) and optional region hint for crop lookup
const LOCATIONS = [
  { name: 'Nagpur', district: 'Nagpur', lat: 21.1458, lng: 79.0882, region: 'Vidarbha' },
  { name: 'Bhandara', district: 'Bhandara', lat: 21.1800, lng: 79.5800, region: 'Vidarbha' },
  { name: 'Chandrapur', district: 'Chandrapur', lat: 19.9530, lng: 79.2961, region: 'Vidarbha' },
  { name: 'Gadchiroli', district: 'Gadchiroli', lat: 20.1125, lng: 80.0567, region: 'Vidarbha' },
  { name: 'Yavatmal', district: 'Yavatmal', lat: 20.3886, lng: 78.1276, region: 'Vidarbha' },
  { name: 'Kolhapur', district: 'Kolhapur', lat: 16.7050, lng: 74.2433, region: 'Western Maharashtra' },
  { name: 'Ratnagiri', district: 'Ratnagiri', lat: 16.9902, lng: 73.3129, region: 'Konkan' },
  { name: 'Nashik', district: 'Nashik', lat: 19.9975, lng: 73.7898, region: 'Central Maharashtra' },
];

// NEW: import static data
import { mineralsData, cropsData } from '../data/staticData';

// Define approximate region polygons and colors (visual/educational only)
const REGIONS = [
  {
    name: 'Konkan',
    color: '#06b6d4',
    coords: [
      [16.5, 72.5],
      [16.9, 73.5],
      [18.5, 73.0],
      [19.2, 73.5],
      [18.0, 72.5]
    ]
  },
  {
    name: 'Vidarbha',
    color: '#ef4444',
    coords: [
      [20.8, 78.0],
      [21.8, 79.5],
      [20.9, 80.8],
      [19.0, 80.0],
      [19.5, 78.5]
    ]
  },
  {
    name: 'Western Maharashtra',
    color: '#f59e0b',
    coords: [
      [16.5, 73.5],
      [17.8, 74.5],
      [18.5, 75.3],
      [17.0, 75.6],
      [16.2, 74.8]
    ]
  },
  {
    name: 'Central Maharashtra',
    color: '#10b981',
    coords: [
      [18.5, 73.0],
      [20.0, 73.5],
      [20.0, 75.0],
      [18.0, 75.0]
    ]
  },
  {
    name: 'Marathwada',
    color: '#6366f1',
    coords: [
      [18.5, 75.0],
      [19.6, 76.2],
      [18.8, 77.0],
      [17.2, 76.5]
    ]
  },
  {
    name: 'Deccan Plateau',
    color: '#8b5cf6',
    coords: [
      [17.5, 75.0],
      [18.8, 76.5],
      [17.2, 77.5],
      [16.0, 76.0]
    ]
  },
  {
    name: 'Dry Areas',
    color: '#94a3b8',
    coords: [
      [17.0, 75.5],
      [18.2, 76.8],
      [16.5, 77.2],
      [16.0, 76.0]
    ]
  }
];

export default function MaharashtraMap({ onSelect }) {
	const [selected, setSelected] = useState(null);
	const [details, setDetails] = useState({});
	const [loading, setLoading] = useState(false);
	const [err, setErr] = useState('');
	const [hoveredDistrict, setHoveredDistrict] = useState(null);
	const [hoverCache, setHoverCache] = useState({});
	// NEW: region hover cache for crops
	const [hoveredRegion, setHoveredRegion] = useState(null);
	const [regionHoverCache, setRegionHoverCache] = useState({});

	// Maharashtra bounds (lat, lng) — approximate
	const mahaBounds = [
		[14.5, 72.0], // SW
		[22.9, 81.5]  // NE
	];

	// Use local static data instead of fetch
	const loadDetails = (loc) => {
		setErr('');
		setLoading(true);
		setDetails({});
		try {
			const minerals = mineralsData.filter(m => (m.districts || []).includes(loc.district));
			const crops = loc.region
				? cropsData.filter(c => (c.region || '').toLowerCase().includes(loc.region.toLowerCase()))
				: cropsData.filter(c => (c.region || '').toLowerCase().includes(loc.district.toLowerCase()));

			setDetails({ minerals, crops });
			setLoading(false);

			// notify parent
			if (typeof onSelect === 'function') {
				onSelect({ district: loc.district, region: loc.region, minerals, crops });
			}
		} catch (e) {
			console.error(e);
			setErr('Could not load details from static data.');
			setDetails({});
		} finally {
			setLoading(false);
		}
	};

	const loadHoverDetails = async (loc) => {
		if (!loc) return;
		const key = loc.district;
		if (hoverCache[key]) {
			setHoveredDistrict(key);
			return;
		}
		const minerals = mineralsData.filter(m => (m.districts || []).includes(key));
		setHoverCache(prev => ({ ...prev, [key]: minerals }));
		setHoveredDistrict(key);
	};

	// NEW: load crops for a region on hover and cache results
	const loadRegionHover = (regionName) => {
		if (!regionName) return;
		if (regionHoverCache[regionName]) {
			setHoveredRegion(regionName);
			return;
		}
		// local lookup using cropsData
		const crops = cropsData.filter(c => (c.region || '').toLowerCase().includes(regionName.toLowerCase()));
		setRegionHoverCache(prev => ({ ...prev, [regionName]: crops }));
		setHoveredRegion(regionName);
	};

	const clearHover = () => {
		setHoveredDistrict(null);
		setHoveredRegion(null);
	};

	const onMarkerClick = (loc) => {
		setSelected(loc);
		loadDetails(loc);
	};

	// When region polygon clicked, provide crops for that region
	const onRegionClick = (regionName) => {
		const crops = getCropsForRegion(regionName);
		if (typeof onSelect === 'function') {
			onSelect({ district: null, region: regionName, minerals: [], crops });
		}
	};

	// robust image candidates for minerals/crops (tries .jpeg -> .jpg -> .png -> .svg -> placeholder)
	const imageCandidates = (name, folder = 'minerals') => {
		const slug = name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
		const nameSlug = name.replace(/[()\s\/\\,]+/g, '-').replace(/[^a-z0-9-]+/gi, '').toLowerCase();
		return [
			`/images/${folder}/${slug}.jpeg`,
			`/images/${folder}/${slug}.jpg`,
			`/images/${folder}/${slug}.png`,
			`/images/${folder}/${nameSlug}.jpeg`,
			`/images/${folder}/${nameSlug}.jpg`,
			`/images/${folder}/${nameSlug}.png`,
			`/images/${folder}/${slug}.svg`,
			`/images/${folder}/placeholder.svg`
		];
	};

	// new img element builder props: initial src and an onError handler to cycle candidates
	const imgPropsFor = (name, folder = 'minerals', w = 96, h = 64) => {
		const candidates = imageCandidates(name, folder);
		return {
			src: candidates[0],
			'data-candidates': JSON.stringify(candidates),
			'data-attempt': '0',
			alt: name,
			loading: 'lazy',
			decoding: 'async',
			style: { width: w, height: h, objectFit: 'cover', borderRadius: 6, border: '1px solid #e2e8f0' },
			onError: (e) => {
				const el = e.currentTarget;
				try {
					const list = JSON.parse(el.dataset.candidates || '[]');
					let attempt = parseInt(el.dataset.attempt || '0', 10);
					attempt = Math.min(list.length - 1, attempt + 1);
					el.src = list[attempt];
					el.dataset.attempt = String(attempt);
					// warn when falling back to placeholder (last index)
					if (attempt === list.length - 1 && list[attempt].endsWith('placeholder.svg')) {
						console.warn(`Image not found for "${name}" in /images/${folder}/ — used placeholder.`);
					}
				} catch {
					el.src = `/images/${folder}/placeholder.svg`;
				}
			}
		};
	};

	const getCropsForRegion = (regionName) => {
		if (!regionName) return [];
		return cropsData.filter(c => (c.region || '').toLowerCase().includes(regionName.toLowerCase()));
	};

	return (
		// explicit inline height and minHeight so map is visible even if Tailwind isn't active
		<div style={{ height: '60vh', minHeight: '400px', width: '100%' }} className="w-full rounded shadow overflow-hidden">
			<MapContainer
				center={[19.75, 75.71]}
				zoom={6.5}
				bounds={mahaBounds}
				maxBounds={mahaBounds}
				maxBoundsViscosity={0.9}
				scrollWheelZoom
				style={{ height: '100%', width: '100%' }}
			>
				<TileLayer
					attribution='© OpenStreetMap contributors'
					url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
				/>

				{/* Region polygons (rendered beneath markers) */}
				{REGIONS.map(region => (
					<Polygon
						key={region.name}
						positions={region.coords}
						pathOptions={{
							color: region.color,
							fillColor: region.color,
							fillOpacity: hoveredRegion === region.name ? 0.35 : 0.15,
							weight: 1
						}}
						eventHandlers={{
							mouseover: () => loadRegionHover(region.name),
							mouseout: () => setHoveredRegion(null),
							click: () => onRegionClick(region.name)
						}}
					>
						<Tooltip sticky>
							<div style={{ fontSize: 13, maxWidth: 260 }}>
								<div style={{ fontWeight: 700, marginBottom: 6 }}>{region.name}</div>
								<div style={{ fontSize: 12, color: '#334155' }}>
									{regionHoverCache[region.name] && regionHoverCache[region.name].length ? (
										<div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
											{regionHoverCache[region.name].map(c => (
												<div key={c._id} style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
													<img {...imgPropsFor(c.name, 'crops', 56, 40)} />
													<div>
														<div style={{ fontWeight: 600 }}>{c.name}</div>
														<div style={{ fontSize: 12, color: '#475569' }}>{c.use}</div>
													</div>
												</div>
											))}
										</div>
									) : (
										<div style={{ color: '#94a3b8' }}>No crop data for this region</div>
									)}
								</div>
							</div>
						</Tooltip>
					</Polygon>
				))}

				{/* Markers (existing behavior) */}
				{LOCATIONS.map(loc => (
					<Marker
						key={loc.district}
						position={[loc.lat, loc.lng]}
						eventHandlers={{
							click: () => onMarkerClick(loc),
							mouseover: () => loadHoverDetails(loc),
							mouseout: () => clearHover()
						}}
					>
						{/* Tooltip shows mineral info (hover cache) */}
						<Tooltip direction="top" offset={[0, -10]} opacity={0.95}>
							<div style={{ maxWidth: 320, minWidth: 200 }}>
								<div style={{ fontWeight: 600 }}>{loc.name} ({loc.district})</div>
								<div style={{ fontSize: 12, color: '#334155', marginTop: 6 }}>
									{hoveredDistrict === loc.district ? (
										(hoverCache[loc.district] && hoverCache[loc.district].length) ? (
											(() => {
												const arr = hoverCache[loc.district];
												const primary = arr[0];
												const others = arr.slice(1);
												return (
													<div>
														{/* primary image */}
														<div style={{ display: 'flex', gap: 8, alignItems: 'flex-start', marginBottom: 8 }}>
															<img {...imgPropsFor(primary.name, 'minerals', 96, 64)} />
															<div style={{ fontSize: 12 }}>
																<div style={{ fontWeight: 700 }}>{primary.name}</div>
																<div style={{ color: '#475569', marginTop: 4 }}>{primary.use}</div>
															</div>
														</div>
														{/* others */}
														{others.length > 0 && (
															<div style={{ fontSize: 12, marginTop: 6 }}>
																<div style={{ fontWeight: 600, marginBottom: 4 }}>Also:</div>
																<div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
																	{others.map(m => (
																		<div key={m._id} style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
																			<img {...imgPropsFor(m.name, 'minerals', 36, 26)} />
																			<div style={{ fontSize: 12 }}>{m.name}</div>
																		</div>
																	))}
																</div>
															</div>
														)}
													</div>
												);
											})()
										) : (
											<div style={{ fontSize: 12, color: '#94a3b8' }}>No mineral data</div>
										)
									) : (
										<div style={{ fontSize: 12, color: '#64748b' }}>Hover to load details — mineral image and short info will appear here.</div>
									)}
								</div>
							</div>
						</Tooltip>

						{selected && selected.district === loc.district && (
							<Popup minWidth={260}>
								<div style={{ minWidth: 220 }}>
									<strong>{loc.name} ({loc.district})</strong>
									<div style={{ marginTop: 8 }}>
										<div style={{ fontWeight: 700 }}>Minerals</div>
										{details.minerals && details.minerals.length ? details.minerals.map(m => (
											<div key={m._id} style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 6 }}>
												<img {...imgPropsFor(m.name, 'minerals', 48, 36)} />
												<div>
													<div style={{ fontWeight: 600 }}>{m.name}</div>
													<div style={{ fontSize: 12 }}>{m.use}</div>
												</div>
											</div>
										)) : <div className="text-sm text-slate-400">No minerals recorded</div>}

										<div style={{ marginTop: 10, fontWeight: 700 }}>Crops</div>
										{details.crops && details.crops.length ? details.crops.map(c => (
											<div key={c._id} style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 6 }}>
												<img {...imgPropsFor(c.name, 'crops', 48, 36)} />
												<div>
													<div style={{ fontWeight: 600 }}>{c.name}</div>
													<div style={{ fontSize: 12 }}>{c.region} — {c.use}</div>
												</div>
											</div>
										)) : <div className="text-sm text-slate-400">No crops recorded</div>}
									</div>
								</div>
							</Popup>
						)}
					</Marker>
				))}
			</MapContainer>
		</div>
	);
}
